import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    ReactFlowProvider,
} from "@xyflow/react";
import { AnimatePresence } from "framer-motion";
import "@xyflow/react/dist/style.css";

import CanvasNode from "./nodes/CanvasNode";
import TriggerNode from "./nodes/TriggerNode";
import ConditionalNode from "./nodes/ConditionalNode";
import StickyNoteNode from "./nodes/StickyNoteNode";
import DataFlowEdge from "./edges/DataFlowEdge";
import { WorkflowCanvasProvider, useCanvasContext } from "./WorkflowCanvasContext";
import { useWorkflowExecution } from "../../../context/WorkflowExecutionContext";
import WorkflowStartOverlay from "./WorkflowStartOverlay";
import NodeDetailOverlay from "./NodeDetailOverlay";
import TriggerDetailOverlay from "./TriggerDetailOverlay";
import CanvasToolbar from "./CanvasToolbar";
import AutomationUsageBanner from "../../components/AutomationUsageBanner";
import ExecutionPanel from "./ExecutionPanel";

const nodeTypes = {
    canvas: CanvasNode,
    trigger: TriggerNode,
    conditional: ConditionalNode,
    stickyNote: StickyNoteNode,
};

const edgeTypes = {
    dataFlow: DataFlowEdge,
};

const defaultEdgeOptions = {
    type: "dataFlow",
    animated: false,
};

const arrowMarker = (
    <svg className="absolute w-0 h-0">
        <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#d1d5db" />
            </marker>
        </defs>
    </svg>
);

function CanvasInner() {
    const {
        nodes,
        edges,
        onNodesChange,
        onEdgesChange,
        onNodeDragStop,
        selectedNodeId,
        setSelectedNodeId,
    } = useCanvasContext();
    const { pausedStepId, setPausedStepId } = useWorkflowExecution();
    const [showExecution, setShowExecution] = useState(true);

    // Auto-open the NDV when the engine pauses at a step (e.g. HTML form)
    useEffect(() => {
        if (pausedStepId) {
            setSelectedNodeId(pausedStepId);
            setShowExecution(false);
            setPausedStepId(null);
        }
    }, [pausedStepId, setSelectedNodeId, setPausedStepId]);

    const onNodeClick = useCallback((_event, node) => {
        if (!node.id.startsWith("note-")) setSelectedNodeId(node.id);
    }, [setSelectedNodeId]);

    const onPaneClick = useCallback(() => {
        setSelectedNodeId(null);
    }, [setSelectedNodeId]);

    const hasStepNodes = nodes.some((n) => n.type !== "stickyNote");
    const isTriggerSelected = selectedNodeId?.startsWith("trigger-");
    const isStepSelected = selectedNodeId && !isTriggerSelected;
    const proOptions = useMemo(() => ({ hideAttribution: true }), []);

    const handleCloseExecution = useCallback(() => setShowExecution(false), []);

    return (
        <div className="w-full h-full flex flex-col">
            <CanvasToolbar onShowExecution={() => setShowExecution(true)} />
            <AutomationUsageBanner />
            <div className="flex-1 bg-gray-50 relative overflow-hidden">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    defaultEdgeOptions={defaultEdgeOptions}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClick}
                    onNodeDragStop={onNodeDragStop}
                    onPaneClick={onPaneClick}
                    snapToGrid
                    snapGrid={[16, 16]}
                    fitView
                    fitViewOptions={{ maxZoom: 1, padding: 0.2 }}
                    minZoom={0.1}
                    maxZoom={4}
                    connectionRadius={60}
                    colorMode="light"
                    proOptions={proOptions}
                >
                    <Background variant="dots" gap={16} color="#d1d5db" />
                    <MiniMap
                        pannable
                        zoomable
                        position="bottom-left"
                        style={{ backgroundColor: "#f9fafb" }}
                        nodeColor="#e5e7eb"
                        maskColor="rgba(255,255,255,0.6)"
                    />
                    <Controls position="bottom-left" style={{ left: 170 }} />
                </ReactFlow>
                {arrowMarker}
                {!hasStepNodes && <WorkflowStartOverlay />}
                <AnimatePresence mode="wait">
                    {isStepSelected && <NodeDetailOverlay key="step" />}
                    {isTriggerSelected && <TriggerDetailOverlay key="trigger" />}
                </AnimatePresence>
                <AnimatePresence>
                    {showExecution && <ExecutionPanel key="exec" onClose={handleCloseExecution} />}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default function WorkflowCanvas() {
    return (
        <ReactFlowProvider>
            <WorkflowCanvasProvider>
                <CanvasInner />
            </WorkflowCanvasProvider>
        </ReactFlowProvider>
    );
}
